// Agendamentos de Multi-Cenário CLT × PJ · lista, cria, roda agora, exclui.
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { CalendarClock, Plus, Trash2, PlayCircle, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const DEFAULT_SCENARIOS = [
  { name: "Júnior", salario_clt: 3500, valor_pj: 4900 },
  { name: "Pleno",  salario_clt: 7000, valor_pj: 9800 },
  { name: "Sênior", salario_clt: 15000, valor_pj: 21000 },
];

export default function CltPjScheduledReportsPanel({ companyId, baseInputs }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // formulário
  const [name, setName] = useState("");
  const [cron, setCron] = useState("0 9 1 * *");
  const [scenarios, setScenarios] = useState(DEFAULT_SCENARIOS);
  const [destinatarios, setDestinatarios] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clt-pj/scheduled-reports");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setName(""); setCron("0 9 1 * *");
    setScenarios(DEFAULT_SCENARIOS);
    setDestinatarios(""); setSubject(""); setMessage("");
    setOpen(true);
  };

  const save = async () => {
    const emails = destinatarios.split(/[,;\s]+/).map(e => e.trim()).filter(e => e.includes("@"));
    if (!name.trim() || !cron.trim() || !emails.length) {
      toast.error("Preencha nome, cron e ao menos 1 e-mail.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(), schedule_cron: cron.trim(),
        company_id: companyId || null,
        scenarios: scenarios.map(s => ({
          name: s.name,
          salario_clt: Number(s.salario_clt),
          valor_pj: Number(s.valor_pj),
        })),
        base_inputs: baseInputs || null,
        destinatarios: emails,
        subject: subject || null,
        message: message || null,
      };
      const { data } = await api.post("/clt-pj/scheduled-reports", body);
      setItems((prev) => [data, ...prev]);
      setOpen(false);
      toast.success(`Agendamento "${data.name}" criado.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runNow = async (sch) => {
    if (!window.confirm(`Executar agora "${sch.name}" e enviar e-mails?`)) return;
    try {
      const { data } = await api.post(`/clt-pj/scheduled-reports/${sch.id}/run-now`);
      if (data.status === "sent") toast.success(`Enviado para ${data.sent?.length || 0} destinatário(s).`);
      else if (data.status === "skipped_no_resend_key") toast.warning("RESEND_API_KEY vazia — e-mail não saiu.");
      else toast.error(`Status: ${data.status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (sch) => {
    if (!window.confirm(`Excluir agendamento "${sch.name}"?`)) return;
    try {
      await api.delete(`/clt-pj/scheduled-reports/${sch.id}`);
      setItems((prev) => prev.filter(x => x.id !== sch.id));
      toast.success("Excluído.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="clt-pj-scheduled-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
          <CalendarClock className="h-4 w-4 text-[#0F3B6B]" />
          Agendamentos · Multi-Cenário por E-mail
        </h3>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={load} className="h-7 text-[11px]"><RotateCcw className="h-3 w-3 mr-1" /> Atualizar</Button>
          <Button size="sm" onClick={openNew} className="h-7 text-[11px] bg-[#065F46] hover:bg-[#054d38] text-white"
                  data-testid="btn-new-schedule">
            <Plus className="h-3 w-3 mr-1" /> Novo agendamento
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-20" />
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500 text-[12px]">
          Nenhum agendamento configurado. Crie um para enviar automaticamente um multi-cenário por e-mail (ex.: dia 1 de cada mês).
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`sched-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-medium text-slate-800 truncate">{s.name}</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 text-[9.5px] font-mono border border-amber-200">
                    ⏱ {s.schedule_cron}
                  </span>
                  <span className="text-[10.5px] text-slate-500">
                    {(s.scenarios || []).length} cenário(s) · {(s.destinatarios || []).length} destinatário(s)
                  </span>
                </div>
                <div className="text-[10.5px] text-slate-500">
                  {s.last_run_at
                    ? <>Última execução: {fmtDateTime(s.last_run_at)} ({s.last_run_status || "—"})</>
                    : "Ainda não executado"}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => runNow(s)} className="h-7 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-700"
                      data-testid={`run-${s.id}`}>
                <PlayCircle className="h-3 w-3 mr-1" /> Rodar agora
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del(s)} className="h-7 w-7 p-0 text-rose-500"
                      data-testid={`del-sched-${s.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Dialog open onOpenChange={(v) => !v && setOpen(false)}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-[#234935]">Novo agendamento · Multi-Cenário CLT × PJ</DialogTitle>
              <DialogDescription>
                O e-mail HTML com resumo dos cenários será enviado automaticamente conforme o cron (UTC).
                Parâmetros fixos (RAT/FAP, Terceiros, Regime, ISS...) são herdados da simulação ativa.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Nome *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Multi-Cenário Mensal · Diretoria" data-testid="sched-name" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Cron (UTC) *</Label>
                  <Input value={cron} onChange={(e) => setCron(e.target.value)}
                         placeholder="0 9 1 * *" className="font-mono" data-testid="sched-cron" />
                  <div className="text-[10px] text-slate-500 mt-0.5">min hora dia mês dia_sem · Ex.: 0 9 1 * * = dia 1 às 09h</div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Assunto do e-mail</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                         placeholder="[TheoKratos] Multi-Cenário Mensal" data-testid="sched-subject" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Destinatários * (vírgula ou nova linha)</Label>
                <Textarea rows={2} value={destinatarios} onChange={(e) => setDestinatarios(e.target.value)}
                          placeholder="diretoria@empresa.com, cfo@empresa.com"
                          data-testid="sched-dests" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Mensagem (opcional)</Label>
                <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)}
                          data-testid="sched-message" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Cenários</Label>
                <div className="space-y-1.5">
                  {scenarios.map((sc, i) => (
                    <div key={i} className="grid grid-cols-[1fr_120px_120px_32px] gap-1.5 items-center">
                      <Input value={sc.name} onChange={(e) => setScenarios(scenarios.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                             placeholder="Nome" className="h-7 text-[11px]" />
                      <Input type="number" value={sc.salario_clt}
                             onChange={(e) => setScenarios(scenarios.map((x, j) => j === i ? { ...x, salario_clt: e.target.value } : x))}
                             placeholder="Salário CLT" className="h-7 text-[11px] font-mono" />
                      <Input type="number" value={sc.valor_pj}
                             onChange={(e) => setScenarios(scenarios.map((x, j) => j === i ? { ...x, valor_pj: e.target.value } : x))}
                             placeholder="Valor PJ" className="h-7 text-[11px] font-mono" />
                      <Button size="sm" variant="ghost" onClick={() => setScenarios(scenarios.filter((_, j) => j !== i))}
                              className="h-7 w-7 p-0 text-rose-500">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setScenarios([...scenarios, { name: `Cenário ${scenarios.length + 1}`, salario_clt: 5000, valor_pj: 7000 }])}
                          className="h-6 text-[10.5px]">
                    <Plus className="h-3 w-3 mr-1" /> Cenário
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
              <Button onClick={save} disabled={saving}
                      className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                      data-testid="btn-save-schedule">
                {saving ? "Salvando..." : "Criar agendamento"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
